import app from 'flarum/forum/app';
import PostList from 'flarum/forum/components/PostList';
import { DeckPostListState, asQuery, refillIfEmpty } from '../states/deckListStates';
import { POSTED, POST_RESTORED, REMOVED, STARTED, discussionOf, postOf } from './realtimeEvents';
const CHECK_LIMIT = 10;

/**
 * A newest-first column of comments, backed by its own PostListState, so any
 * post filter the API understands works. Post ids only grow, so the newest id
 * on screen is the key for asking "anything newer?".
 */
export default class PostListSource {
  key = 0;
  constructor(params) {
    this.state = new DeckPostListState({
      sort: 'newest',
      ...params
    });
  }
  load() {
    if (this.state.hasItems()) return Promise.resolve();
    return this.prepare().then(() => this.state.refresh()).then(() => this.snapshot());
  }

  /** Runs once before the first load, for columns whose params need looking up. */
  prepare() {
    return Promise.resolve();
  }
  view() {
    return m(PostList, {
      state: this.state
    });
  }
  async checkForNew() {
    return (await this.newerPosts()).filter(post => post.user() !== app.session.user).length;
  }
  async applyNew() {
    const posts = await this.newerPosts();
    const extra = this.state.extraPosts;

    // The list is newest first; insert oldest first so the newest ends on top.
    posts.sort((a, b) => Number(a.id()) - Number(b.id())).forEach(post => extra.unshift(post));
    this.key = Math.max(this.key, ...posts.map(post => Number(post.id())));
    return posts.length;
  }
  showNew() {
    return this.state.revalidate().then(() => this.snapshot());
  }

  /**
   * A new reply that certainly belongs here is inserted from the payload; one
   * that certainly doesn't is ignored; only undecidable ones ask the server.
   * A new discussion's first post isn't in its Started payload, so a column
   * that may want it asks.
   */
  onRealtime(event) {
    const post = postOf(event);
    if (event.name === REMOVED) {
      return event.payload.data.type === 'posts' && this.removePost(String(event.payload.data.id)) ? 'updated' : undefined;
    }

    // A restored post goes back where it was in time, which only the server can place.
    if (event.name === POST_RESTORED && post) {
      return this.shows(post) ? 'updated' : this.matches(post) === false ? undefined : 'check';
    }
    if (event.name === POSTED && post) {
      const match = this.matches(post);
      if (match === false || post.contentType() !== 'comment') return;
      if (match === undefined) return 'check';
      return this.insert(post) ? 'inserted' : undefined;
    }
    if (event.name === STARTED) {
      const discussion = discussionOf(event);
      return discussion && this.mayMatchNewDiscussion(discussion) !== false ? 'check' : undefined;
    }

    // Edits and likes: the store already has the change.
    if (post && this.shows(post)) return 'updated';
  }

  /** For a prune() that takes posts out: the next page fills an emptied column. */
  refill() {
    return refillIfEmpty(this.state);
  }

  /** Whether a post belongs in this column: undefined when only the server can tell. */
  matches(post) {
    return undefined;
  }

  /** Whether a new discussion's first post might belong here: false rules it out. */
  mayMatchNewDiscussion(discussion) {
    return undefined;
  }
  shows(post) {
    return this.state.getPages().some(page => page.items.some(item => item.id() === post.id()));
  }
  removePost(id) {
    const state = this.state;
    let removed = false;
    [...state.pages, {
      items: state.extraPosts
    }].forEach(page => {
      const index = page.items.findIndex(item => item.id() === id);
      if (index !== -1) {
        page.items.splice(index, 1);
        removed = true;
      }
    });
    return removed;
  }
  insert(post) {
    if (this.shows(post)) return false;
    this.state.extraPosts.unshift(post);
    this.key = Math.max(this.key, Number(post.id()));
    return true;
  }

  /** The column's own request, newest first: whatever it asks for, includes and all, the new posts have too. */
  async newerPosts() {
    const params = this.state.requestParams();
    const latest = await app.store.find('posts', {
      ...asQuery(params),
      sort: '-createdAt',
      page: {
        limit: CHECK_LIMIT,
        total: 0
      }
    });
    return latest.filter(post => Number(post.id()) > this.key);
  }
  snapshot() {
    this.key = Math.max(0, ...this.state.getPages().flatMap(page => page.items).map(post => Number(post.id())));
  }
}
flarum.reg.add('flarum-deck', 'forum/columns/PostListSource', PostListSource);